import React from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";

import {
    fetchProfiles,
    fetchProfileColumns,
    setSelectedIds as setSelectedIdsAction,
    setSearchString as setSearchStringAction,
    setPageNumber as setPageNumberAction,
    setLimitNumber as setLimitNumberAction,
    setSortData as setSortDataAction,
    setFilterData as setFilterDataAction,
    setError as setErrorAction,
} from "../../../../reducers/profileSlice";

import useFilters from "../../../common/hooks/useFilters";
import useReduxTable from "../../../common/hooks/useReduxTable";
import useRiskLevels from "../../../common/hooks/useRiskLevels";
import CommonTable from "../../../common/table";
import { ProgressDialog } from "../../../common/dialogs";

import { ErrorsDialog } from "../../../common/dialogs";

import "./styles.less";


const getCellValue = (row, column, intl, riskLevels) => {
    switch (column.id) {
        case "systemId":
            return row.system;

        case "critProfileName":
            return row.name;

        case "riskLevelId": {
            const riskLevel = riskLevels.find(riskLevel => riskLevel.id === row.riskLevel);
            return riskLevel?.description || row.riskLevel;
        }
        case "updUserName":
            return row.updUser;

            case "matrices":
                return row.matrices.map(matrix => matrix.key).join(", ");

        case "active":
            return row.active ?
                intl.formatMessage({ id: "common.table.active.true" }) :
                intl.formatMessage({ id: "common.table.active.false" });

        default:
            return row[column.id];
    }
};

const ProfileListPage = () => {
    const intl = useIntl();
    const navigate = useNavigate();

    const riskLevels = useRiskLevels();

    const {
        rows, columns,
        total, busy,
        selectedIds,
        searchString, setSearchString,
        pageNumber, setPageNumber,
        limitNumber, setLimitNumber,
        filterData, setFilterData,
        sortData, setSortData,
        error, setError
    } = useReduxTable(
        (state) => state.profiles.profiles, fetchProfiles,
        (state) => state.profiles.columns, fetchProfileColumns,
        (state) => state.profiles.selectedIds, setSelectedIdsAction,
        (state) => state.profiles.searchString, setSearchStringAction,
        (state) => state.profiles.pageNumber, setPageNumberAction,
        (state) => state.profiles.limitNumber, setLimitNumberAction,
        (state) => state.profiles.sortData, setSortDataAction,
        (state) => state.profiles.filterData, setFilterDataAction,
        (state) => state.profiles.error, setErrorAction,
        (state) => state.profiles.profilesTotal,
        (state) => state.profiles.busy,
    );

    const busyType = useSelector((state) => state.profiles.busyType);

    const getFilters = useFilters("critical-profile");

    const handleAddProfileClick = () => {
        navigate("form")
    };

    const handleRowClick = (profile) => {
        const encodedName = encodeURIComponent(profile.name);
        navigate(`form/${encodedName}/${profile.system}`);
    };

    const getTableRowId = (row) => `${row.system}${row.name}`;

    const getCellValueInner = (row, column) => getCellValue(row, column, intl, riskLevels);

    const toolbarActions = [
        {
            id: "title",
            title: intl.formatMessage({ id: "profile-list-page.toolbar.text.title" }),
            type: "text",
            position: "begin"
        },
        {
            id: "add",
            title: intl.formatMessage({ id: "profile-list-page.toolbar.btn-create-profile" }),
            callback: handleAddProfileClick,
            position: "end"
        }
    ];

    return (
        <>
            <CommonTable
                rows={rows}
                columns={columns}
                filterData={filterData}
                setFilterData={setFilterData}
                sortData={sortData}
                setSortData={setSortData}
                toolbarActions={toolbarActions}
                selectedIds={selectedIds}
                getRowId={getTableRowId}
                pageNumber={pageNumber}
                setPageNumber={setPageNumber}
                limitNumber={limitNumber}
                setLimitNumber={setLimitNumber}
                total={total}
                onRowClick={handleRowClick}
                searchString={searchString}
                setSearchString={setSearchString}
                getCellValue={getCellValueInner}
                getFilters={getFilters}
            />

            <ProgressDialog
                open={busy}
                busyType={busyType}
            />

            <ErrorsDialog
                error={error}
                open={error?.type === "error"}
                onClose={() => setError(null)}
            />
        </>
    )
};

export default ProfileListPage;